import { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

/**
 * アプリの操作に関するカスタムフック
 */
export const useApp = (userId) => {
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // loadApps を useCallback でメモ化
  const loadApps = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.getLatestApps(userId);
      setApps(response.apps || []);
    } catch (err) {
      setError('アプリの読み込みに失敗しました');
      console.error('Failed to load apps:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // 新しいAppIdを取得する関数
  const getNewAppId = async () => {
    try {
      const response = await api.getNewAppId();
      console.log('New app ID:', response.appId);
      return response.appId;
    } catch (err) {
      console.error('Failed to get new app ID:', err);
      throw err;
    }
  };

  // createApp 関数の修正
  const createApp = async (recipeText, userId, appId, userName = '', shared = "true") => {
    // ここでは setLoading(true) を呼び出さない
    try {
      const response = await api.createApp(recipeText, userId, appId, userName, shared);
      return response; // runId を含むレスポンスを返す
    } catch (error) {
      console.error('Error creating app:', error);
      throw error;
    }
    // ここでも setLoading(false) を呼び出さない
  };

  // アプリを削除する
  const removeApp = async (appId) => {
    if (!userId || !appId) return false;
    
		// ユーザーに確認する
		const isConfirmed = window.confirm('アプリを削除しますか？');
		if (!isConfirmed) return false;

    try {
      await api.deleteApp(appId, userId);
      setApps(apps.filter(app => app.id !== appId));
      return true;
    } catch (err) {
      setError('アプリの削除に失敗しました');
      console.error('Failed to delete app:', err);
      return false;
    }
  };

  // アプリを取得する - アクセス制御のためにuserIdを渡す
  const getApp = async (appId) => {
    if (!appId) return null;

    setLoading(true);
    setError(null);

    try {
      // アクセス制御チェックのためuserIdを渡す
      const app = await api.getAppById(appId, userId);
      setSelectedApp(app);
      return app;
    } catch (err) {
      console.error('Failed to get app:', err);

      // トライアル期限切れエラーの場合
      if (err.code === 'TRIAL_EXPIRED') {
        setError(err.message);
        // エラーをそのまま再スローして、呼び出し側で処理できるようにする
        throw err;
      }

      setError('アプリの取得に失敗しました');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // いいねを追加する
  const addLike = async (appId) => {
    if (!appId) return null;
    
    try {
      const response = await api.likeApp(appId);
      
      // 選択中のアプリならいいね数を更新
      if (selectedApp && selectedApp.id === appId) {
        setSelectedApp({
          ...selectedApp,
          likes: response.likes
        });
      }
      
      // アプリ一覧のいいね数も更新
      setApps(apps.map(app => 
        app.id === appId ? { ...app, likes: response.likes } : app
      ));
      
      return response.likes;
    } catch (err) {
      console.error('Failed to like app:', err);
      return null;
    }
  };

  // いいねを取り消す関数
  const removeLike = async (appId) => {
    if (!appId) return null;
    
    try {
      const data = await api.removeLike(appId, userId);
      
      // 選択中のアプリならいいね数を更新
      if (selectedApp && selectedApp.id === appId) {
        setSelectedApp({
          ...selectedApp,
          likes: data.likes
        });
      }
      
      // アプリ一覧を更新
      setApps(apps.map(app => 
        app.id === appId ? { ...app, likes: data.likes } : app
      ));
      
      return data.likes;
    } catch (err) {
      console.error('Failed to remove like:', err);
      return null;
    }
  };

  // 初期ロード
  useEffect(() => {
    if (userId) {
      loadApps();
    }
  }, [userId, loadApps]);

  return {
    apps,
    selectedApp,
    loading,
    error,
    loadApps,
    createApp,
    removeApp,
    getApp,
    addLike,
    removeLike,  // 新しい関数を追加
    setSelectedApp,
    getNewAppId, // 新しい関数を追加
  };
};